import { datadogLogs } from '@datadog/browser-logs';
import { logContext } from '@shared/services';
import { useMutation } from '@tanstack/react-query';
import { MealPlanInstructionsEntity } from '@web-app/db';
import { useApiClient } from '@web-app/hooks/useApiClient';

export interface GenerateMealPlanInstructionsParams {
  dailyRoomName: string;
  healthieClientId?: number;
  healthieProviderId?: number;
  sourceAppointmentId?: string;
  force?: boolean;
}

const generateMealPlanInstructions = async (
  params: GenerateMealPlanInstructionsParams,
  apiClient: ReturnType<typeof useApiClient>,
): Promise<MealPlanInstructionsEntity> => {
  const {
    dailyRoomName,
    healthieClientId,
    healthieProviderId,
    sourceAppointmentId,
    force,
  } = params;

  try {
    const queryParams = new URLSearchParams();
    if (healthieClientId)
      queryParams.set('healthieClientId', String(healthieClientId));
    if (healthieProviderId)
      queryParams.set('healthieProviderId', String(healthieProviderId));
    if (force) queryParams.set('force', 'true');
    const qs = queryParams.toString();

    const response = await apiClient.post(
      `/meal-plan/${dailyRoomName}/generate-meal-plan-instructions${qs ? `?${qs}` : ''}`,
      { sourceAppointmentId },
    );

    const data = response.data as MealPlanInstructionsEntity;

    datadogLogs.logger.info(
      'Meal plan instructions generated successfully',
      logContext('meal-plan', 'generate-instructions', {
        dailyRoomName,
        healthieClientId: data.healthieClientId,
        healthieProviderId: data.healthieProviderId,
        sourceAppointmentId,
        apiStatus: data.apiStatus,
        force: !!force,
      }),
    );

    return data;
  } catch (error) {
    datadogLogs.logger.error(
      'Failed to generate meal plan instructions',
      logContext('meal-plan', 'generate-instructions', {
        dailyRoomName,
        err:
          error instanceof Error
            ? {
                message: error.message,
                stack: error.stack,
              }
            : String(error),
        force: !!force,
      }),
    );
    throw error;
  }
};

export const useGenerateMealPlanInstructions = () => {
  const apiClient = useApiClient();

  return useMutation({
    mutationFn: (params: GenerateMealPlanInstructionsParams) =>
      generateMealPlanInstructions(params, apiClient),
    retry: 2,
    retryDelay: 2000,
    onSuccess: (data, variables) => {
      datadogLogs.logger.info(
        'Meal plan instructions mutation completed successfully',
        logContext('meal-plan', 'generate-instructions', {
          dailyRoomName: variables.dailyRoomName,
          healthieClientId: data.healthieClientId,
          healthieProviderId: data.healthieProviderId,
          apiStatus: data.apiStatus,
          force: !!variables.force,
        }),
      );
    },
    onError: (error, variables) => {
      datadogLogs.logger.error(
        'Meal plan instructions mutation failed after retries',
        logContext('meal-plan', 'generate-instructions', {
          dailyRoomName: variables.dailyRoomName,
          err:
            error instanceof Error
              ? {
                  message: error.message,
                  stack: error.stack,
                }
              : String(error),
          force: !!variables.force,
        }),
      );
    },
  });
};
